/*
  High-level service for managing common alerting workflows, such as showing
  incident modals.
*/
import qs from 'query-string';
import { ngRoute } from '../../../app/routing/ngRoute';

const CROSS_APP_NAVIGATION_STORAGE_KEY = 'navigation-history-previous-page';

class AlertingService {
    constructor(eventService, eventModal, alertTypeService, confirmService) {
        this.eventService = eventService;
        this.eventModal = eventModal;
        this.alertTypeService = alertTypeService;
        this.confirmService = confirmService;
    }

    getDashboardNavigationUrl(pathname, searchParams = {}) {
        const sanitizedSearchParams = { ...searchParams };
        delete sanitizedSearchParams.eventId;
        delete sanitizedSearchParams.incidentId;
        delete sanitizedSearchParams.viewTime;

        const search = qs.stringify(sanitizedSearchParams);

        return `${pathname}${search ? `?${search}` : ''}`;
    }

    createAlertDetailsNavigationContext({ dashboardName = 'Dashboard', dashboardUrl } = {}) {
        if (!dashboardUrl) {
            return undefined;
        }

        return {
            dashboardName,
            dashboardUrl,
        };
    }

    navigateToAlertDetails(eventId, navigationContext) {
        const pathname = `/dash/alert-details/${eventId}`;
        const dashboardPath = navigationContext?.dashboardUrl?.replace(/^#/, '');
        const dashboardName = navigationContext?.dashboardName;
        const search = qs.stringify({
            breadcrumbTitle: dashboardName,
            breadcrumbPath: dashboardPath,
        });
        const route = {
            pathname,
            search,
            state: dashboardPath
                ? {
                      breadcrumb: {
                          label: dashboardName,
                          pathname: dashboardPath,
                      },
                  }
                : undefined,
        };

        if (dashboardPath) {
            try {
                sessionStorage.setItem(
                    CROSS_APP_NAVIGATION_STORAGE_KEY,
                    JSON.stringify({
                        path: dashboardPath,
                        title: dashboardName || document.title || 'Dashboard',
                    })
                );
            } catch {
                // Ignore storage failures and continue with navigation.
            }
        }

        if (ngRoute.history) {
            ngRoute.history.push(route);
            return;
        }

        window.location.hash = `${pathname}${search ? `?${search}` : ''}`;
    }

    openIncidentModal(alert, navigationContext) {
        const incidentId = alert.incidentId || alert.sf_incidentId;
        const firingStateQuery = this.alertTypeService.getFiringStateQueryStr();

        return this.eventService
            .search({
                query: 'incidentId:"' + incidentId + '" AND is:(' + firingStateQuery + ')',
                orderBy: '-sf_timestamp',
                limit: 1,
                offset: 0,
            })
            .then((data) => {
                if (data && data.length) {
                    return data[0];
                } else {
                    // cant find event? should not happen in real life.
                    throw new Error('Event cannot be found');
                }
            })
            .then((event) => {
                if (
                    navigationContext?.redirectToAlertDetails &&
                    event?.id &&
                    navigationContext?.dashboardUrl
                ) {
                    this.navigateToAlertDetails(event.id, navigationContext);
                    return undefined;
                }

                return this.eventModal(event);
            })
            .catch((error) => {
                // show error message
                return this.confirmService.confirm({
                    title: 'Error',
                    text: [error.message],
                    yesText: 'Close',
                    noText: '',
                });
            });
    }
}

AlertingService.$inject = ['eventService', 'eventModal', 'alertTypeService', 'confirmService'];

export default AlertingService;
